import { useEffect, useState } from "react"
import { createServer } from "../../api/servers.js"
import { listSoftwareVersions, listLoaderVersions, refreshServerCatalog } from "../../api/serverCatalog.js"
import { friendlyError } from "../../api/errorMessages.js"
import { Button } from "../ui/Button.js"
import { Input } from "../ui/Input.js"
import { SearchableVersionInput } from "../ui/SearchableVersionInput.js"
import { useToast } from "../ui/Toast.js"

/**
 * Sugerencias comunes para `serverSoftware`, no un catalogo cerrado --
 * Crafty acepta texto libre (ver @mc/types ServerCreateQuery). "Otro..."
 * revela un Input libre para cualquier valor que Crafty soporte y esta
 * lista no cubra. Confirmado contra la config real de Big Bucket (el jar
 * repository de Crafty, ver TODO.md "Ampliacion") que estas 6 son las que
 * Crafty efectivamente puede construir hoy -- Spigot deliberadamente NO
 * esta en la lista: el propio mantenedor de Crafty confirmo que no esta
 * soportado ("we haven't been able to add it to big bucket yet").
 */
const SOFTWARE_SUGGESTIONS = ["paper", "vanilla", "fabric", "forge", "quilt", "purpur"] as const
const LOADER_BASED_SOFTWARE = new Set(["fabric", "quilt", "forge"]) // catalogo dinamico de loader, ver LoaderVersionProvider.ts
/** Restringen que versiones de Minecraft son validas, ver MinecraftVersionRestrictor.ts -- el resto usa el catalogo general de Mojang. */
const VERSION_RESTRICTED_SOFTWARE = new Set(["paper", "purpur"])
const CUSTOM_SOFTWARE_VALUE = "__custom__"

/**
 * Regla real de Crafty (confirmado en logs reales de server-manager.service,
 * ver TODO.md "Pendiente ahora mismo"): rechaza con INVALID_JSON_SCHEMA (400)
 * si el nombre tiene menos de 2 caracteres o contiene `\`, `/`, o `#`.
 * Validado client-side para no depender del round-trip completo
 * Gateway -> NATS -> Crafty para que el usuario se entere.
 */
const INVALID_NAME_CHARS = /[\\/#]/

function validateServerName(name: string): string | undefined {
  if (name.length < 2) return "El nombre debe tener al menos 2 caracteres."
  if (INVALID_NAME_CHARS.test(name)) return "El nombre no puede contener \\, / ni #."
  return undefined
}

const selectStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: "var(--text-base)",
  borderRadius: "var(--radius-sm)",
  border: "1px solid var(--border-default)",
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
}

function LabeledSelect(props: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  disabled?: boolean
}) {
  const id = props.label.toLowerCase().replace(/\s+/g, "-")
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <label htmlFor={id} style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)" }}>
        {props.label}
      </label>
      <select
        id={id}
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
        style={selectStyle}
      >
        {props.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * Formulario de creacion de servidores (TODO.md "Crear servidores desde la
 * app"). Software es libre (sugerencias + texto libre, Crafty valida del
 * lado suyo); version de Minecraft y version de loader (Fabric/Quilt/Forge)
 * son campos de un conjunto CERRADO y usan SearchableVersionInput (combobox
 * tipo busqueda) contra el catalogo dinamico de routes/serverCatalog.ts,
 * nunca texto libre. La lista de versiones de Minecraft mostrada depende
 * del software elegido (listSoftwareVersions): para Paper/Purpur son solo
 * las versiones que ese software realmente soporta, no todo el catalogo de
 * Mojang -- ver TODO.md "Ampliacion".
 */
export function CreateServerForm({ onCreated, onCancel }: { onCreated: (serverId: string) => void; onCancel: () => void }) {
  const [name, setName] = useState("")
  const [nameTouched, setNameTouched] = useState(false)
  const [softwareChoice, setSoftwareChoice] = useState<string>(SOFTWARE_SUGGESTIONS[0])
  const [customSoftware, setCustomSoftware] = useState("")
  const [minecraftVersion, setMinecraftVersion] = useState("")
  const [loaderVersion, setLoaderVersion] = useState("")
  const [memMinGb, setMemMinGb] = useState(2)
  const [memMaxGb, setMemMaxGb] = useState(4)
  const [port, setPort] = useState(25565)
  const [creating, setCreating] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const [versions, setVersions] = useState<string[]>([])
  const [versionsLoading, setVersionsLoading] = useState(true)
  const [loaderVersions, setLoaderVersions] = useState<{ version: string; stable: boolean }[]>([])
  const [loaderVersionsLoading, setLoaderVersionsLoading] = useState(false)

  const toast = useToast()

  const serverSoftware = softwareChoice === CUSTOM_SOFTWARE_VALUE ? customSoftware : softwareChoice
  const isLoaderBased = LOADER_BASED_SOFTWARE.has(serverSoftware)

  function loadVersionsFor(software: string) {
    setVersionsLoading(true)
    listSoftwareVersions(software || "vanilla")
      .then(({ versions }) => {
        setVersions(versions)
        setMinecraftVersion((current) => (versions.includes(current) ? current : (versions[0] ?? "")))
      })
      .catch((err) => toast.show(friendlyError(err).message, "error"))
      .finally(() => setVersionsLoading(false))
  }

  // Catalogo dinamico de versiones de Minecraft: se re-carga cada vez que
  // cambia el software elegido, porque Paper/Purpur restringen el conjunto
  // valido (ver VERSION_RESTRICTED_SOFTWARE) -- el resto del software usa
  // siempre el mismo catalogo general de Mojang que el Gateway devuelve
  // igual sea cual sea el software (ver routes/serverCatalog.ts).
  useEffect(() => {
    loadVersionsFor(serverSoftware)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSoftware])

  // Catalogo dinamico de loader version: para software con catalogo
  // confirmado (fabric/quilt/forge, ver LOADER_BASED_SOFTWARE), y solo una
  // vez que hay una version de Minecraft elegida (el loader depende de ella).
  useEffect(() => {
    if (!isLoaderBased || !minecraftVersion) {
      setLoaderVersions([])
      return
    }
    let cancelled = false
    setLoaderVersionsLoading(true)
    listLoaderVersions(serverSoftware, minecraftVersion)
      .then(({ versions }) => {
        if (cancelled) return
        setLoaderVersions(versions)
        if (versions.length > 0) setLoaderVersion((current) => current || (versions[0]?.version ?? ""))
      })
      .catch((err) => {
        if (!cancelled) toast.show(friendlyError(err).message, "error")
      })
      .finally(() => {
        if (!cancelled) setLoaderVersionsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaderBased, serverSoftware, minecraftVersion])

  async function handleRefreshCatalog() {
    setRefreshing(true)
    try {
      await refreshServerCatalog()
      toast.show("Catalogo actualizado.", "success")
      loadVersionsFor(serverSoftware)
      setLoaderVersions([])
      setLoaderVersion("")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setRefreshing(false)
    }
  }

  async function handleCreate() {
    setNameTouched(true)
    if (validateServerName(name)) return
    setCreating(true)
    try {
      const { serverId } = await createServer({
        name,
        installMethod: "download_jar",
        minecraftVersion,
        serverSoftware,
        loaderVersion: isLoaderBased ? loaderVersion || null : null,
        memMinGb,
        memMaxGb,
        port,
      })
      toast.show("Servidor creado.", "success")
      onCreated(serverId)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setCreating(false)
    }
  }

  const nameError = validateServerName(name)
  const canSubmit = Boolean(
    !nameError && serverSoftware && minecraftVersion && memMinGb > 0 && memMaxGb >= memMinGb && port > 0,
  )

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--brand-primary)",
        background: "var(--bg-surface-raised)",
        maxWidth: 480,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong style={{ fontSize: "var(--text-lg)" }}>Nuevo servidor</strong>
        <Button variant="ghost" size="sm" loading={refreshing} onClick={handleRefreshCatalog}>
          Actualizar catalogo
        </Button>
      </div>

      <Input
        label="Nombre"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => setNameTouched(true)}
        error={nameTouched ? nameError : undefined}
      />

      <LabeledSelect
        label="Software del servidor"
        value={softwareChoice}
        onChange={setSoftwareChoice}
        options={[
          ...SOFTWARE_SUGGESTIONS.map((s) => ({ value: s, label: s })),
          { value: CUSTOM_SOFTWARE_VALUE, label: "Otro..." },
        ]}
      />
      {softwareChoice === CUSTOM_SOFTWARE_VALUE && (
        <Input label="Software (texto libre)" value={customSoftware} onChange={(e) => setCustomSoftware(e.target.value)} />
      )}

      <SearchableVersionInput
        label="Version de Minecraft"
        value={minecraftVersion}
        onChange={setMinecraftVersion}
        loading={versionsLoading}
        options={versions.map((v) => ({ value: v, label: v }))}
      />

      {isLoaderBased && (
        <SearchableVersionInput
          label="Version del loader"
          value={loaderVersion}
          onChange={setLoaderVersion}
          loading={loaderVersionsLoading}
          options={loaderVersions.map((v) => ({ value: v.version, label: v.stable ? v.version : `${v.version} (inestable)` }))}
        />
      )}

      <div style={{ display: "flex", gap: "var(--space-3)" }}>
        <Input
          label="RAM min (GB)"
          type="number"
          min={1}
          value={memMinGb}
          onChange={(e) => setMemMinGb(Number(e.target.value))}
        />
        <Input
          label="RAM max (GB)"
          type="number"
          min={1}
          value={memMaxGb}
          onChange={(e) => setMemMaxGb(Number(e.target.value))}
        />
      </div>

      <Input label="Puerto" type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} />

      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        <Button loading={creating} disabled={!canSubmit} onClick={handleCreate}>
          Crear
        </Button>
        <Button variant="ghost" disabled={creating} onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}
